// Tabs/DangerZoneTab.tsx
import { useState } from 'react';
import {
  Alert, Button, Card, Group, List, Modal, Stack, Text, TextInput, ThemeIcon,
} from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { useACCourseService, ACCourse } from '@actschurch/ccms-sdk';
import { toast } from '@/components/ui/providers/AlertProvider';

interface DangerZoneTabProps {
  course: ACCourse | null;
  /** Called after a successful delete so the page can navigate away. */
  onDeleted: () => void;
}

/**
 * Danger Zone — destructive course actions, kept off the list page and out of the header.
 *
 * Deleting a course was previously a one-click red icon on every row of `/Grow/Courses`, which is a
 * lot of blast radius for a mis-click: sessions, intake dates, facilitator assignments and custom
 * form definitions all hang off a course. It now lives behind this tab and a type-the-name
 * confirmation, on a course you are already looking at.
 */
export default function DangerZoneTab({ course, onDeleted }: DangerZoneTabProps) {
  const courseService = useACCourseService();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [typedName, setTypedName] = useState('');
  const [deleting, setDeleting] = useState(false);

  const courseName = course?.name ?? '';
  const nameMatches = typedName.trim() === courseName.trim() && courseName.trim().length > 0;

  const handleDelete = async () => {
    if (!course || !nameMatches) return;
    setDeleting(true);
    try {
      await courseService.delete(course.courseID);
      toast.warning('Course Deleted', `"${course.name}" has been deleted.`);
      setConfirmOpen(false);
      onDeleted();
    } catch (e) {
      console.error('Error deleting course:', e);
      toast.error('Error', 'Could not delete the course.');
    } finally {
      setDeleting(false);
    }
  };

  const closeConfirm = () => {
    if (deleting) return;
    setConfirmOpen(false);
    setTypedName('');
  };

  return (
    <Stack gap="md">
      <Card withBorder radius="md" padding="lg" style={{ borderColor: 'var(--mantine-color-red-6)' }}>
        <Group gap="sm" align="flex-start" wrap="nowrap">
          <ThemeIcon size={40} radius="md" variant="light" color="red">
            <IconAlertTriangle size={20} />
          </ThemeIcon>

          <Stack gap={4} style={{ flex: 1 }}>
            <Text fw={700}>Delete this course</Text>
            <Text size="sm" c="dimmed">
              This cannot be undone. Deleting a course also affects everything attached to it:
            </Text>
            <List size="sm" c="dimmed" spacing={2} withPadding>
              <List.Item>its session templates and intake dates</List.Item>
              <List.Item>its facilitator assignments</List.Item>
              <List.Item>its custom form definition and any answers captured against it</List.Item>
            </List>
            <Text size="sm" c="dimmed" mt={4}>
              Registrations already created from this course may be left without a parent. Consider
              setting the course to an inactive status instead.
            </Text>
          </Stack>

          <Button
            color="red"
            variant="light"
            leftSection={<IconTrash size={16} />}
            onClick={() => setConfirmOpen(true)}
            disabled={!course}
          >
            Delete Course
          </Button>
        </Group>
      </Card>

      <Modal
        opened={confirmOpen}
        onClose={closeConfirm}
        title={
          <Group gap="xs">
            <IconTrash size={18} color="var(--mantine-color-red-6)" />
            <Text fw={600}>Delete Course</Text>
          </Group>
        }
        centered
        size="md"
      >
        <Stack gap="md">
          <Alert color="red" variant="light" icon={<IconAlertTriangle size={16} />}>
            This permanently deletes <strong>{courseName || 'this course'}</strong> and everything
            attached to it.
          </Alert>

          <TextInput
            label={`Type "${courseName}" to confirm`}
            placeholder={courseName}
            value={typedName}
            onChange={(e) => setTypedName(e.currentTarget.value)}
            autoComplete="off"
            data-autofocus
          />

          <Group justify="flex-end" gap="sm">
            <Button variant="subtle" onClick={closeConfirm} disabled={deleting}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={handleDelete}
              loading={deleting}
              disabled={!nameMatches}
            >
              Delete permanently
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
